import { NgZone } from "@angular/core";
import DOMPurify from "dompurify";
import Handsontable, { CellCoords } from "handsontable";
import { NestedHeaderSettings } from "handsontable/plugins/nestedHeaders";
import _, { isNil } from "lodash";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { CellFormatter, Cellstyle, ClickableCell, HotCell } from "./cell";
import { Expander, IExpanderSettings } from "./expander";
import { HotCellAngularRendererService } from "./hot-cell-angular-renderer.service";
import { HotRow } from "./row";
import { SelSum } from "./selSum";

export interface IHotColumnGroup {
    label: string;
    parent?: IHotColumnGroup;
}

export interface IHotColumnDefinition {
    width?: number;
    readOnly?: boolean;
    cellName: string;
    type?: string;
    header: string;
    editor?: boolean;
    group?: IHotColumnGroup;
    source?: any[];
}

export interface IRowState {
    expanded?: boolean;
}

type ColumnLevel = { column: IHotColumnGroup; count: number; padding: number }[];

/**
 * Specifika regler som vi kör en opt-in för att få kunna ta del av
 * Detta görs för att få en bra överblick över vilka egenskaper vi faktiskt använder
 */

// Alla inställningar för HotDoc
export interface IHotDocSettings extends Handsontable.GridSettings {
    columns?: Handsontable.ColumnSettings[]; //Overridar columns så vi alltid får en array av ColumnSettings
    raskSettings?: IHotDocRaskSettings;
    raskInfra?: IHotDocRaskInfraSettings;
}

// Rask-specifika inställningar/specialare till HotDoc som gäller per instans
export interface IHotDocRaskSettings {
    expander?: IExpanderSettings;
    calculateSum?: boolean;
    afterOnCellMouseDown?: (event: MouseEvent, coords: CellCoords, TD: HTMLTableCellElement) => void;
    numericParser?: (value: any) => number;
}

// Angular-specifik hantering av HotDoc inställningar som kräver tillgång till Angular-relaterade saker
export interface IHotDocRaskInfraSettings {
    ngZone?: NgZone;
    hotCellAngularRendererService?: HotCellAngularRendererService;
}
export class HotDoc {
    readonly settings: IHotDocSettings = {
        width: "100%",
        height: "auto",
        rowHeaders: false,
        colHeaders: true,
        readOnlyCellClassName: "hot-readonly",
        className: "rask-hot head-middle",
        fillHandle: false,
        raskSettings: {
            numericParser: (value) => parseInt(value, 10)
        },
        raskInfra: {},
        sanitizer: (content: string, source: string) => {
            if (source === "CopyPaste.paste") {
                return DOMPurify.sanitize(content, {
                    ADD_ATTR: ["content"],
                    FORCE_BODY: true
                });
            }
            return DOMPurify.sanitize(content);
        }
    };

    hotInstance: Handsontable;
    sumOfSelected: number;

    readonly rows: HotRow[] = [];

    private readonly rowState: IRowState[] = [];
    private columnDefinitions: IHotColumnDefinition[];
    private isDestroyed = false;
    // Handsontables nestedRows-plugin bygger sina fysiska radindex genom att platta ut
    // träd-datat (rows + __children) på djupet. Denna lista speglar exakt samma ordning
    // så att index-baserade uppslag (docRenderer, click, findCell, beforeChange) blir korrekta
    // oavsett om nestedRows används eller ej.
    private flattenedRows: HotRow[] = [];

    constructor(
        private readonly columnsFactory: () => IHotColumnDefinition[],
        private readonly rowsFactory: (doc: HotDoc) => void,
        private readonly extraSettings?: (doc: HotDoc) => IHotDocSettings
    ) {
        const customSettings = this.extraSettings?.(this) || null;
        if (customSettings) {
            _.merge(this.settings, customSettings);
        }

        if (this.settings.nestedRows) {
            // Handsontable kräver rowHeaders aktiverat för att kunna rita ut expand/collapse-kontrollerna
            this.settings.rowHeaders = true;
            this.applyNestedRowsHeaderRenderer();
        }

        this.settings.beforeChange = this.beforeChange;
        this.settings.beforeViewRender = this.beforeViewRender;
        this.settings.afterDestroy = this.afterDestroy;
        this.settings.beforeOnCellMouseDown = this.beforeOnCellMouseDown;
        this.settings.afterOnCellMouseDown = this.afterOnCellMouseDown;
        if (this.settings.raskSettings.calculateSum) {
            this.settings.afterSelection = this.afterSelection;
            this.settings.afterDeselect = this.afterDeselect;
        }
        this.settings.afterDocumentKeyDown = this.onKeydown;

        this.columnDefinitions = this.buildColumnDefinitions();
        this.settings.columns = this.buildColumns();
    }

    init(hotInstance: Handsontable) {
        this.hotInstance = hotInstance;
        this.initHeaders();
        this.rebuild();
        this.bind();
        this.scheduleInitialCollapse();
    }

    createExpander = (id: number | string) => {
        let state = this.rowState[id];
        if (!state) {
            state = {};
            this.rowState[id] = state;
        }

        return new Expander(state, this.settings.raskSettings.expander);
    };

    createRow = (init: (row: HotRow) => void, style?: any): HotRow => {
        const row = new HotRow(style);
        init(row);
        this.rows.push(row);
        return row;
    };

    createChildRow = (parentRow: HotRow, init: (row: HotRow) => void, style?: any): HotRow => {
        const row = new HotRow(style);
        init(row);
        if (!parentRow.__children) {
            parentRow.__children = [];
        }
        parentRow.__children.push(row);
        return row;
    };

    bind = (): void => {
        this.columnDefinitions = this.buildColumnDefinitions();
        this.settings.columns = this.buildColumns();
        this.settings.colHeaders = this.buildColHeaders();
        this.settings.data = this.rows;
        this.settings.mergeCells = this.buildMergeCells();
        this.flattenedRows = this.buildFlattenedRows();
        this.updateSettings();
    };

    // Kollapsar alla toppnivårader när nestedRows är aktiverat, så grupper är stängda som standard
    // (motsvarar tidigare Expander-beteende där expanded === false initialt). Ska bara köras vid
    // initial uppstart, inte vid varje rebind, eftersom expand/collapse-tillståndet därefter ägs
    // och hanteras av Handsontables egen nestedRows-plugin.
    collapseAllNestedRows = (): void => {
        if (!this.settings.nestedRows || !this.hotInstance) {
            return;
        }
        const nestedRowsPlugin = this.hotInstance.getPlugin("nestedRows") as any;
        nestedRowsPlugin?.collapseAll?.();
    };

    rebind = (): void => {
        this.initDoc();
        this.bind();
    };

    render = (): void => {
        if (this.isDestroyed) {
            return;
        }

        if (this.hotInstance) {
            this.updateSettings();
            this.hotInstance.render();
        }
    };

    selectedCells = (): any[] => new SelSum(this.hotInstance).getSelection();

    deselectAllCells = (): void => {
        if (this.hotInstance) {
            this.hotInstance.deselectCell();
        }
    };

    // Handsontables nestedRows-plugin bygger om sin interna collapsingUI (disable/enable) varje
    // gång updateSettings() körs med nestedRows-nyckeln satt, vilket händer varje gång doc.bind()
    // anropas. hot-doc.component.ts:s Angular-livscykel (ngOnChanges + signal-effect) hinner anropa
    // init()/bind() flera gånger i rad vid uppstart, så ett enstaka collapseAllNestedRows()-anrop
    // (även efter att updateSettings faktiskt tillämpats) kan bli överskrivet av en senare,
    // överlappande updateSettings-cykel. Vi löser detta genom att lyssna på Handsontables egen
    // afterRender-hook och köra om collapseAllNestedRows() vid varje rendering under en kort
    // startperiod, tills tillståndet hunnit stabiliseras - därefter kopplas hooken bort så att den
    // inte stör användarens egna expand/collapse-klick.
    private readonly scheduleInitialCollapse = (): void => {
        if (!this.settings.nestedRows || !this.hotInstance) {
            return;
        }

        const retryUntil = Date.now() + 1000;
        const tryCollapse = () => {
            this.collapseAllNestedRows();
            if (Date.now() >= retryUntil) {
                this.hotInstance.removeHook("afterRender", tryCollapse);
            }
        };
        this.hotInstance.addHook("afterRender", tryCollapse);
    };

    // Byter ut Handsontables inbyggda plus/minus-knapp för expand/collapse mot en chevron-ikon
    // (samma utseende som den gamla Expander-mekanismen) genom att linda in den befintliga
    // rad-header-renderern via afterGetRowHeaderRenderers. En ny renderers-array byggs upp av
    // Handsontable vid varje rendering, så det finns ingen risk att linda in samma funktion flera
    // gånger. Den ursprungliga renderern körs oförändrad (den utlöser i sin tur nestedRows-pluginets
    // egen afterGetRowHeader-hook som skapar .ht_nestingButton-elementet), varefter vi injicerar vår
    // egen ikon i den knappen.
    private readonly applyNestedRowsHeaderRenderer = (): void => {
        const existingHook = this.settings.afterGetRowHeaderRenderers;
        this.settings.afterGetRowHeaderRenderers = (renderers: ((...args: unknown[]) => unknown)[]) => {
            existingHook?.(renderers);

            const originalRenderer = renderers[0];
            if (!originalRenderer) {
                return;
            }
            renderers[0] = (...args: unknown[]) => {
                originalRenderer(...args);

                const TH = args[1] as HTMLTableCellElement;
                const button = TH.querySelector<HTMLElement>(".ht_nestingButton");
                if (!button) {
                    return undefined;
                }
                let icon = button.querySelector<HTMLElement>("i.ht-nesting-chevron");
                if (!icon) {
                    icon = document.createElement("i");
                    icon.className = "far fa-chevron-up rotate ht-nesting-chevron";
                    button.appendChild(icon);
                }
                icon.classList.toggle("collapsed", button.classList.contains("ht_nestingExpand"));
                return undefined;
            };
        };
    };

    private readonly rebuild = () => {
        if (!this.hotInstance) {
            return;
        }

        this.initDoc();
        this.flattenedRows = this.buildFlattenedRows();
        this.hotInstance.updateData(this.rows);
    };

    private readonly updateSettings = () => {
        window.requestAnimationFrame(() => {
            if (this.hotInstance) {
                const mergeCellsPlugin = this.hotInstance.getPlugin("mergeCells");
                try {
                    // skapar en kopia av settings utan renderAllRows för att den inte får ändras på efter att tabellen initierats
                    mergeCellsPlugin.clearCollections();
                    const {
                        renderAllRows,
                        sanitizer,
                        ...updatableSettings
                    } = this.settings;

                    this.hotInstance.updateSettings(updatableSettings);
                } catch (error) {
                    //Detta görs för problem med handsontable
                    //se bug:216545
                    const message = error.message as string;
                    if (!message.includes("startRow")) {
                        throw error;
                    }
                    console.warn("startRow fel hanterat", error);
                }
            }
        });
    };

    private readonly initDoc = (): void => {
        this.rows.splice(0);
        this.rowsFactory(this);
    };

    // Bygger en djup-först-utplattad lista av rows + __children i samma ordning som
    // Handsontables nestedRows-plugin använder för sina fysiska radindex.
    private readonly buildFlattenedRows = (): HotRow[] => {
        const flattened: HotRow[] = [];
        const visit = (rowsToVisit: HotRow[]) => {
            rowsToVisit.forEach(row => {
                flattened.push(row);
                if (row.__children && row.__children.length > 0) {
                    visit(row.__children);
                }
            });
        };
        visit(this.rows);
        return flattened;
    };

    // Slår upp en HotRow utifrån ett visuellt radindex från Handsontable, oavsett om
    // nestedRows (eller annan trimning/sortering) gör att visuellt och fysiskt index skiljer sig åt.
    private readonly rowAt = (visualRow: number): HotRow => {
        const physicalRow = this.hotInstance ? this.hotInstance.toPhysicalRow(visualRow) : visualRow;
        return this.flattenedRows[physicalRow];
    };

    private readonly buildColumnDefinitions = (): IHotColumnDefinition[] => {
        const columnDefinitions = this.columnsFactory();
        const expander = this.settings.raskSettings.expander;
        if (expander) {
            // expander kräver uttryckligt kolumnindex
            columnDefinitions.splice(expander.col, 0, {
                header: "",
                width: 30,
                readOnly: true,
                cellName: expander.name
            });
        }
        return columnDefinitions;
    };

    private readonly afterOnCellMouseDown = (event: MouseEvent, coords: CellCoords, td: HTMLTableCellElement): void => {
        if (this.settings.raskSettings?.afterOnCellMouseDown) {
            this.settings.raskInfra.ngZone.run(() => this.settings.raskSettings.afterOnCellMouseDown(event, coords, td));
        }
    };

    private readonly beforeOnCellMouseDown = (event: Event, coords: any, TD: Element) => {
        const row = this.rowAt(coords.row);

        if (row === undefined) {
            return;
        }

        const cell = row.cells[coords.col] as ClickableCell;
        const isClickableCell = cell instanceof ClickableCell;
        if (!isClickableCell) {
            return;
        }
        if (cell.stopImmediatePropagationActive) {
            event.stopImmediatePropagation();
        }
        this.settings.raskInfra.ngZone.run(() => cell.onClick(this));
    };

    private readonly onKeydown = (event: KeyboardEvent) => {
        if (event.code === "Delete" || event.code === "Backspace") {
            const selected = this.selectedCells();
            selected.forEach(s => {
                const cell = this.findCell(s);
                cell?.onDelete(this.settings);
            });
            this.rebuild();
        }
    };

    private findCell(indexPair: any[]): HotCell {   // [rowidx, colidx]
        return !isNil(indexPair) &&
            Array.isArray(indexPair) &&
            indexPair.length >= 2 ? (this.rowAt(indexPair[0])?.cells?.[indexPair[1]] ?? null) : null;
    }

    private readonly afterSelection = () => {
        const selSum = new SelSum(this.hotInstance);
        this.sumOfSelected = selSum.getSelectedSum();
    };

    private readonly afterDeselect = () => {
        this.sumOfSelected = null;
    };

    // Sker före varje refresh/utritning av handsontable
    private readonly beforeViewRender = () => {
        if (!this.hotInstance) {
            return;
        }
        // Destroya alla dynamiskt skapade RaskAngular-komponenter (som skedde förra utritningen) om det finns några
        this.settings.raskInfra.hotCellAngularRendererService.destroyAllComponentsForHotInstance(this.hotInstance);
    };

    // Sker när Handsontable destroyas
    private readonly afterDestroy = () => {
        this.settings.raskInfra.hotCellAngularRendererService.destroyAllComponentsForHotInstance(this.hotInstance);
        this.isDestroyed = true;
    };

    private readonly beforeChange = (changes: any[], source: string) => {
        const changesSubject = new Subject<void>();
        const debounceRebuildChanges$ = changesSubject.pipe(debounceTime(10));

        debounceRebuildChanges$.subscribe(() => this.rebuild());

        const allchanges = changes.map((change) => {
            const [
                rowIndex,
                propertyName,
                oldValue,
                newValue
            ] = change;
            return {
                rowIndex,
                propertyName,
                oldValue,
                newValue
            };
        });

        allchanges.forEach((c, index) => {
            const row = this.rowAt(c.rowIndex);
            if (!row) {
                changes[index] = null;
                return;
            }
            const cellName = c.propertyName?.split ? c.propertyName?.split(".")[1] : c.propertyName;
            const cell = row.cells[cellName];

            const accept = cell && cell.onChange && cell.validate(c.newValue);
            if (!accept) {
                changes[index] = null;
                return;
            }

            const changeArg = cell.changeArgumentFactory(c.newValue, () => {
                changesSubject.next();
            }, this.settings);

            cell.onChange(changeArg);
        });
    };

    private readonly docRenderer = (hot: any, td: HTMLTableCellElement, row: any, col: any, prop: any, value: any, cellProperties: any): HTMLTableCellElement => {
        const cellName = prop.split(".")[1];
        const currentRow = this.rowAt(row);
        if (!currentRow) {
            return td;
        }

        const currentCell = currentRow.cells[cellName];
        if (currentCell && currentCell.renderer) {
            return currentCell.renderer(hot, td, row, col, prop, value, cellProperties);
        }

        CellFormatter.applyStyle(Cellstyle.Readonly, td, cellProperties);

        td.innerHTML = value || "";
        return td;
    };

    private readonly initHeaders = () => {
        const hasNestedHeaders = this.columnDefinitions.some(c => c.group);

        if (hasNestedHeaders) {
            this.settings.nestedHeaders = this.buildNestedHeaders();
            this.settings.colHeaders = true;
        } else {
            this.settings.colHeaders = this.buildColHeaders();
        }
    };

    private getParentGroups(level: ColumnLevel) {
        return _.chain(level)
            .map(entry => entry.column)
            .filter(column => column.parent !== undefined)
            .uniqBy(column => column.parent)
            .map(column => ({
                column: column.parent,
                count: level
                    .filter(entry => entry.column.parent === column.parent)
                    .length,
                padding: level
                    .findIndex(entry => entry.column === column)
            }))
            .value();
    }

    private buildNestedHeaders(): NestedHeaderSettings[][] {
        let currentLevel: ColumnLevel = _.chain(this.columnDefinitions)
            .map((c, i) => ({
                column: {
                    label: c.header,
                    parent: c.group
                },
                count: 1,
                padding: i
            }))
            .value();

        const levels: ColumnLevel[] = [];
        while (currentLevel.length > 0) {
            levels.unshift(currentLevel);
            currentLevel = this.getParentGroups(currentLevel);
        }

        return levels.map(level => level.reduce((previousValue, entry) => {
            const padding = entry.padding - previousValue.length;

            return [
                ...previousValue,
                ...Array(padding).fill(""),
                {
                    label: entry.column.label,
                    colspan: entry.count
                }
            ];
        }, []));
    }

    private readonly buildColHeaders = (): any[] => this.columnDefinitions.map(columnDef => columnDef.header);

    private readonly buildColumns = (): any[] => this.columnDefinitions.map(columnDef => columnDef.type === "dropdown" ? {
        editor: "select",
        selectOptions: columnDef.source,
        renderer: this.docRenderer,
        data: `cells.${columnDef.cellName}.value`
    } : {
        width: columnDef.width,
        readOnly: columnDef.readOnly,
        data: `cells.${columnDef.cellName}.value`,
        type: columnDef.type,
        editor: columnDef.editor,
        renderer: this.docRenderer
    });

    private readonly buildMergeCells = (): any[] => {
        const colDefs = this.columnDefinitions;
        const ret = [];

        this.rows.forEach((row, rowIndex) => {
            colDefs.forEach((coldef, index) => {
                const cell = row.cells[coldef.cellName] as HotCell;
                if (!cell || !cell.settings) {
                    return;
                }

                const rowspan = cell.settings.rowSpan || 1;
                const colspan = cell.settings.colSpan || 1;
                if (rowspan === 1 && colspan === 1) {
                    return;
                }

                ret.push({
                    row: rowIndex,
                    col: index,
                    rowspan: rowspan,
                    colspan: colspan
                });
            });
        });
        return ret;
    };
}
